import { useMutation } from '@apollo/client';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { UpdateControlTestOversightResultApiMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateControlTestOversightResultApiDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateControlTestOversightResultTRPC } from './useUpdateControlTestOversightResultTRPC';

export type UpdateControlTestOversightResultWrapperInput = {
  Id: string;
  // ParentControlId + OriginalTimestamp are required by the legacy Apollo
  // `UpdateTestResultInput`; the tRPC path ignores OriginalTimestamp (no
  // optimistic-concurrency guard — NotFound on 0 rows instead).
  ParentControlId: string;
  OriginalTimestamp?: string | null;
  Title?: string | null;
  TestType?: TestType | null;
  Description?: string | null;
  DesignEffectiveness?: number | null;
  PerformanceEffectiveness?: number | null;
  OverallEffectiveness?: number | null;
  Submitter?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateControlTestOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateControlTestOversightResultApiDocument,
    {
      update: (cache) => {
        evictField(cache, 'control_test_oversight_result');
        evictField(cache, 'control');
        evictField(cache, 'oversight_assessment');
      },
    }
  );

  const trpcMutation = useUpdateControlTestOversightResultTRPC();

  const updateControlTestOversightResult = async (
    input: UpdateControlTestOversightResultWrapperInput
  ): Promise<UpdateControlTestOversightResultApiMutation> => {
    if (trpcEnabled) {
      const { OriginalTimestamp: _OriginalTimestamp, ...trpcInput } = input;

      return trpcMutation.updateControlTestOversightResult(trpcInput);
    }

    const result = await updateGraphQL({
      variables: {
        object: {
          Id: input.Id,
          ParentControlId: input.ParentControlId,
          OriginalTimestamp: input.OriginalTimestamp,
          Title: input.Title,
          TestType: input.TestType,
          Description: input.Description,
          DesignEffectiveness: input.DesignEffectiveness,
          PerformanceEffectiveness: input.PerformanceEffectiveness,
          OverallEffectiveness: input.OverallEffectiveness,
          Submitter: input.Submitter,
          TestDate: input.TestDate,
          CustomAttributeData: input.CustomAttributeData ?? undefined,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update control test oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateControlTestOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateControlTestOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
